// IIFE embed entry — this is what the client drops into their page with a
// single <script> tag. See README.md "Embedding" for the host integration
// contract.
//
// Goals:
//   - Zero dependency on the host page's DOM (no #app, no module system).
//   - Idempotent: double-inclusion is a no-op.
//   - Runtime configurable via either:
//       • <script data-app-id="…" data-bot-id="…" data-debug="true" …>
//       • window.FinteplaChat = { appId, botId, … } set BEFORE the tag.

import { setupKommunicateUi, triggerSpeakToPersonAction } from './kommunicate.js'
import { finteplaVideoDispatcher } from './video-dispatcher.js'
import { finteplaVideoDeeplinkBootstrap } from './video-deeplink.js'
import { resolveConfig } from './config.js'
import { configureAnalytics, rotateAnalyticsSessionId, track } from './analytics/index.js'
import { installCtaDelegation } from './analytics/cta-delegation.js'

const MOUNT_FLAG = '__finteplaChatMounted'

// Capture the script element synchronously, during IIFE execution. Once the
// bundle finishes evaluating, `document.currentScript` reverts to null.
const currentScript =
  typeof document !== 'undefined' ? document.currentScript : null

function readScriptConfig() {
  if (!currentScript) return {}
  const d = currentScript.dataset || {}
  const out = {}
  if (d.appId) out.appId = d.appId
  if (d.botId) out.botId = d.botId
  if (d.headerTitle) out.headerTitle = d.headerTitle
  if (d.speakToPersonText) out.speakToPersonText = d.speakToPersonText
  if (d.debug === 'true') out.debug = true
  if (d.concealMaxMs != null && d.concealMaxMs !== '') {
    const n = Number(d.concealMaxMs)
    if (Number.isFinite(n) && n > 0) out.concealMaxMs = n
  }
  if (d.analyticsEndpoint) out.analyticsEndpoint = d.analyticsEndpoint
  if (d.analyticsEnabled === 'false') out.analyticsEnabled = false
  if (d.analyticsEnabled === 'true') out.analyticsEnabled = true
  if (d.environment) out.environment = d.environment
  if (d.debugAnalytics === 'true') out.debugAnalytics = true
  return out
}

function readGlobalConfig() {
  const g = typeof window !== 'undefined' ? window.FinteplaChat : null
  return g && typeof g === 'object' ? g : {}
}

function mount() {
  if (typeof window === 'undefined') return
  if (window[MOUNT_FLAG]) {
    console.warn('[Fintepla] Embed already mounted; ignoring duplicate include.')
    return
  }
  window[MOUNT_FLAG] = true

  // script data-* wins over window.FinteplaChat: the script tag is the most
  // explicit, visible-in-markup contract for the host.
  const overrides = { ...readGlobalConfig(), ...readScriptConfig() }

  // Resolve once and boot the analytics transport BEFORE any other
  // subsystem. `resolveConfig` is idempotent, so `setupKommunicateUi` can
  // safely re-resolve the same overrides below.
  const cfg = resolveConfig(overrides)
  configureAnalytics(cfg)
  installCtaDelegation()
  track('page_viewed', {
    metadata: {
      mount: {
        environment: cfg.environment,
        analytics_enabled: cfg.analyticsEnabled === true,
      },
    },
  })

  // Expose a console-friendly restart helper so QA (and any future in-page
  // "Start over" control) can rotate the analytics session and kick
  // Kommunicate's logout without reaching into internals.
  window.FinteplaChat_restart = () => {
    rotateAnalyticsSessionId()
    track('conversation_restarted')
    try {
      window.Kommunicate?.logout?.()
    } catch {
      // swallow — analytics event already sent
    }
  }

  // Host-page CTA bridge: lets clients wire their own "Speak to a Person"
  // controls without reaching into iframe internals.
  window.FinteplaChat_speakToPerson = (message = {}) =>
    triggerSpeakToPersonAction(overrides, 'host_page_cta', message)

  // Video click-dispatcher MUST run before the Kommunicate SDK is
  // injected, because the SDK reads `window.kommunicateSettings` during
  // init. See src/video-dispatcher.js and
  // docs/frontend-vimeo-modal-integration.md.
  finteplaVideoDispatcher()

  setupKommunicateUi(overrides)

  // Deeplink bootstrap: opens the correct family-stories modal when the
  // landing URL carries ?video=<slug> (or #video-<slug>). Relies on the
  // `data-video-slug="<slug>"` attribute being rendered by the CMS on
  // each video poster — see docs/fintepla-cms-changes.md (Part 1) for
  // the contract. See src/video-deeplink.js for the bootstrap logic.
  finteplaVideoDeeplinkBootstrap({ debug: overrides.debug === true })
}

mount()

